import styled from "styled-components";
import type {View} from "../../../data/models/View";
import type {Report} from "../../../data/models/Report";
import {PopupV5} from "../popup/PopupV5";
import GlobeIcon from "../icons/globe.svg?react";
import ChevronRightDouble from "../icons/chevron-right-double.svg?react";
import UserIcon from "../icons/user.svg?react";
import {ButtonStyled, ButtonV5} from "../button/ButtonV5";
import {useAppStore} from "../../../StateManager";
import {ButtonContainerStyled} from "../modules/report/create/wizard/ReportWizardV5.style";
import {colorPalette} from "../styles/colorPalette";
import {XyiconFeature} from "../../../generated/api/base";
import {baseDistance, fontSize, fontWeight, VerticalFlex, VerticalFlexStyle} from "../styles/styles";
import {GlobalSharingHeaderComponent} from "./GlobalSharingHeaderComponent";
import {GlobalSharingBodyComponent} from "./GlobalSharingBodyComponent";

export type ViewActionType = "makePrivate" | "makeGlobal" | "modify" | "delete" | "rename" | "create";

interface IGlobalSharingPopupProps {
	readonly viewItems?: (View | Report)[];
	readonly onClose: (saveChanges: boolean) => void;
	readonly viewActionType?: ViewActionType;
	readonly isReports?: boolean;
	readonly onConfirm?: () => void | Promise<void>;
	readonly newName?: string;
}

export const GlobalSharingPopup = (props: IGlobalSharingPopupProps) => {
	const {viewItems, onClose, viewActionType, isReports, onConfirm, newName} = props;
	const appState = useAppStore((state) => state.appState);
	const {organization, user, actions} = appState;
	const selectedItem = viewItems?.[0];
	const globalViewNames = viewItems?.filter((item) => item.isGlobal).map((item) => item.name) ?? [];
	const allViewNames = viewItems?.map((item) => item.name) ?? [];

	const onApplyChanges = async () => {
		switch (viewActionType) {
			case "delete":
				await selectedItem.delete(viewItems);

				if (!isReports) {
					await appState.app.transport.services.view.onModifyActiveView([selectedItem.id], "deleted");
					const isFavorite = (selectedItem as View)?.isFavorite;

					if (isFavorite) {
						await user?.setFavoriteViews(user.favoriteViews);
					}
				}

				await onConfirm?.();
				organization?.setGlobalViews(organization.globalViews);
				break;
			case "modify":
			case "create":
				await onConfirm?.();
				break;
			case "rename": {
				const isValidName = actions.isNameValidForViewOrReport(newName, isReports ? XyiconFeature.Report : selectedItem.itemFeature, selectedItem.id);

				if (isValidName && newName !== selectedItem.name) {
					selectedItem.name = newName;
					selectedItem.update();
				}
				break;
			}
			case "makePrivate":
				await selectedItem.markViewAsPrivate();
				break;
			case "makeGlobal":
				await selectedItem.markViewAsGlobal();
				break;
		}
		onClose(true);
	};

	const renderIcon = () => {
		if (viewActionType === "makeGlobal" && isReports) {
			return (
				<>
					<UserIcon />
					<ChevronRightDouble className="chevron-right-double" />
					<GlobeIcon />
				</>
			);
		}

		if (viewActionType === "makePrivate" && isReports) {
			return (
				<>
					<GlobeIcon />
					<ChevronRightDouble className="chevron-right-double" />
					<UserIcon />
				</>
			);
		}

		return <GlobeIcon />;
	};

	const actionLabels: Record<ViewActionType, string> = {
		delete: isReports ? "Delete" : "Confirm",
		makeGlobal: isReports ? "Promote" : "Confirm",
		makePrivate: isReports ? "Demote" : "Confirm",
		modify: "Confirm",
		rename: "Confirm",
		create: "Confirm",
	};

	const fontColor = isReports ? (viewActionType === "delete" ? "#F5423E" : "#1e88e5") : "#363636";

	return (
		<PopupV5
			onClose={() => onClose(false)}
			label=""
			className="GlobalViewSharing"
			isSmallPopup={true}
			centerOnScreen={true}
			noButtons={true}
			width="488px"
			freezeRoot={true}
			closeOnClickOutside={false}
		>
			<VerticalFlex
				$gap={baseDistance.md}
				$flex="1"
			>
				<HeaderStyled $fontColor={fontColor}>
					<IconWrapperStyled>{renderIcon()}</IconWrapperStyled>
					<GlobalSharingHeaderComponent
						viewActionType={viewActionType}
						isReports={isReports}
					/>
				</HeaderStyled>
				<ContainerStyled>
					<GlobalSharingBodyComponent
						viewActionType={viewActionType}
						isReports={isReports}
						globalItemNames={globalViewNames}
						allItemNames={allViewNames}
						itemCount={viewItems?.length}
					/>
					<ButtonContainerStyled>
						{viewActionType === "modify" && (
							<ButtonV5
								type="secondary"
								label="Cancel"
								className="cancel"
								onClick={() => onClose(false)}
							/>
						)}
						<ButtonV5
							label={actionLabels[viewActionType]}
							className={viewActionType === "delete" ? "delete" : "confirm"}
							onClick={onApplyChanges}
						/>
					</ButtonContainerStyled>
				</ContainerStyled>
			</VerticalFlex>
		</PopupV5>
	);
};

const HeaderStyled = styled.div<{$fontColor: string}>`
	display: flex;
	flex-direction: column;
	align-items: center;
	font-size: 24px;
	font-weight: ${fontWeight.bold};
	text-align: center;
	gap: ${baseDistance.sm};
	line-height: 32px;
	color: ${(props) => props.$fontColor};

	svg {
		width: 32px;
		height: 32px;
		padding: ${baseDistance.xs};
	}
`;

const IconWrapperStyled = styled.div`
	gap: ${baseDistance.md};
	display: flex;
	align-items: center;

	svg {
		width: 40px;
		height: 40px;
	}

	.chevron-right-double {
		color: ${colorPalette.gray.c300};
		width: 24px;
		height: 24px;
		padding: 0;
	}
`;

const ContainerStyled = styled.div`
	${VerticalFlexStyle};
	font-size: ${fontSize.lg};
	line-height: 24px;
	flex: 1;
	gap: ${baseDistance.md};
	justify-content: space-between;

	${ButtonStyled} {
		&.delete {
			background-color: ${colorPalette.negative.c500Primary};
		}
	}

	& > span {
		padding: 0 ${baseDistance.sm};
	}
`;
